import type { EventEvolutionEvent } from "./EventEvolutionTypes";
import {
  type FormatterMap,
  formatValueWithOptionalUnit,
  getWrappedValue,
  normalizeDateTime,
} from "../../helpers/utils";

const toOptionalString = (value: unknown) => {
  const normalizedValue = getWrappedValue(value);

  if (normalizedValue === undefined || normalizedValue === null) {
    return undefined;
  }

  const stringValue = String(normalizeDateTime(normalizedValue) || normalizedValue).trim();

  return stringValue === "" ? undefined : stringValue;
};

export const getFieldText = (value?: unknown) => toOptionalString(value);

const getObjectIdentity = (objectData: any) => {
  const spaceObject = objectData?.spaceObject ?? objectData;

  return {
    id: toOptionalString(spaceObject?.id),
    name: toOptionalString(spaceObject?.name),
    norad: toOptionalString(spaceObject?.noradId),
    cospar: toOptionalString(spaceObject?.cosparId),
    sourceOfInformation: toOptionalString(
      objectData?.sourceOfInformation ?? spaceObject?.sourceOfInformation,
    ),
  };
};

const normalizeEventRecord = (record: any, rootEvent?: any): EventEvolutionEvent => {
  const eventDetails = record?.event ?? rootEvent;
  const targetIdentity = getObjectIdentity(eventDetails?.target);
  const chaserIdentity = getObjectIdentity(eventDetails?.chaser);

  return {
    id: toOptionalString(record?.id),
    recordId:
      toOptionalString(record?.conjunctionIdentifier) ??
      toOptionalString(record?.id),
    conjunctionId: toOptionalString(record?.id),
    conjunctionIdentifier: toOptionalString(record?.conjunctionIdentifier),
    riskLevel: toOptionalString(record?.riskLevel) ?? toOptionalString(record?.risk_level),
    executionTime:
      toOptionalString(record?.executionEpoch) ??
      toOptionalString(record?.detectionTime),
    detectionTime:
      toOptionalString(record?.detectionTime) ??
      toOptionalString(record?.executionEpoch),
    bestCDM: toOptionalString(record?.bestCDM),
    tca: toOptionalString(record?.timeOfCloseApproach),
    target:
      toOptionalString(record?.targetName) ??
      targetIdentity.name ??
      toOptionalString(record?.target?.spaceObject?.name),
    targetId:
      targetIdentity.id ?? toOptionalString(record?.target?.spaceObject?.id),
    targetNorad:
      targetIdentity.norad ??
      toOptionalString(record?.target?.spaceObject?.noradId),
    targetCospar:
      targetIdentity.cospar ??
      toOptionalString(record?.target?.spaceObject?.cosparId),
    targetSourceOfInformation:
      targetIdentity.sourceOfInformation ??
      toOptionalString(record?.target?.sourceOfInformation) ??
      toOptionalString(record?.target?.spaceObject?.sourceOfInformation),
    targetOrbitUpdateTime:
      toOptionalString(record?.targetOrbitUpdateTime) ??
      toOptionalString(record?.target?.creationDate),
    chaser:
      toOptionalString(record?.chaserName) ??
      chaserIdentity.name ??
      toOptionalString(record?.chaser?.spaceObject?.name),
    chaserId:
      chaserIdentity.id ?? toOptionalString(record?.chaser?.spaceObject?.id),
    chaserNorad:
      chaserIdentity.norad ??
      toOptionalString(record?.chaser?.spaceObject?.noradId),
    chaserCospar:
      chaserIdentity.cospar ??
      toOptionalString(record?.chaser?.spaceObject?.cosparId),
    chaserSourceOfInformation:
      chaserIdentity.sourceOfInformation ??
      toOptionalString(record?.chaser?.sourceOfInformation) ??
      toOptionalString(record?.chaser?.spaceObject?.sourceOfInformation),
    chaserOrbitUpdateTime:
      toOptionalString(record?.chaserOrbitUpdateTime) ??
      toOptionalString(record?.chaser?.creationDate),
    minDistance: toOptionalString(record?.missDistance),
    radialDistance: toOptionalString(record?.radialDistance),
    alongTrackDistance: toOptionalString(record?.alongTrackDistance),
    crossTrackDistance: toOptionalString(record?.crossTrackDistance),
    missDistanceThreshold: toOptionalString(record?.missDistanceThreshold),
    radial: toOptionalString(record?.radialDistance),
    radialThreshold: toOptionalString(record?.screeningVolumeRadial),
    along: toOptionalString(record?.alongTrackDistance),
    alongTrackThreshold: toOptionalString(record?.screeningVolumeAlongTrack),
    cross: toOptionalString(record?.crossTrackDistance),
    crossTrackThreshold: toOptionalString(record?.screeningVolumeCrossTrack),
    probabilityThreshold:
      toOptionalString(record?.collisionProbabilityThreshold) ??
      toOptionalString(record?.maximumCollisionProbabilityThreshold),
    collisionProbability: toOptionalString(record?.collisionProbability),
    maximumCollisionProbability:
      toOptionalString(record?.maximumCollisionProbability),
    scaledCollisionProbability:
      toOptionalString(record?.scaledCollisionProbability),
    poc: toOptionalString(record?.collisionProbability),
    mpoc: toOptionalString(record?.maximumCollisionProbability),
    spoc: toOptionalString(record?.scaledCollisionProbability),
  };
};

export const normalizeEventEvolutionEvent = (
  event?: any,
): EventEvolutionEvent | undefined => {
  if (!event) {
    return undefined;
  }

  const normalizedConjunctions = Array.isArray(event?.conjunctions)
    ? event.conjunctions.map((record: any) => normalizeEventRecord(record, event))
    : [];
  const primaryRecord = normalizedConjunctions[0];
  const targetIdentity = getObjectIdentity(event?.target);
  const chaserIdentity = getObjectIdentity(event?.chaser);

  return {
    id: toOptionalString(event?.id),
    recordId:
      primaryRecord?.recordId ??
      toOptionalString(event?.eventId) ??
      toOptionalString(event?.id),
    conjunctionId: primaryRecord?.conjunctionId,
    conjunctionIdentifier: primaryRecord?.conjunctionIdentifier,
    riskLevel:
      toOptionalString(event?.riskLevel) ??
      primaryRecord?.riskLevel ??
      toOptionalString(event?.risk_level),
    executionTime: primaryRecord?.executionTime,
    detectionTime: primaryRecord?.detectionTime,
    bestCDM: primaryRecord?.bestCDM,
    tca: primaryRecord?.tca,
    target: targetIdentity.name ?? primaryRecord?.target,
    targetId: targetIdentity.id ?? primaryRecord?.targetId,
    targetNorad: targetIdentity.norad ?? primaryRecord?.targetNorad,
    targetCospar: targetIdentity.cospar ?? primaryRecord?.targetCospar,
    targetSourceOfInformation:
      targetIdentity.sourceOfInformation ??
      primaryRecord?.targetSourceOfInformation,
    targetOrbitUpdateTime:
      primaryRecord?.targetOrbitUpdateTime ??
      toOptionalString(event?.target?.latestUpdate),
    chaser: chaserIdentity.name ?? primaryRecord?.chaser,
    chaserId: chaserIdentity.id ?? primaryRecord?.chaserId,
    chaserNorad: chaserIdentity.norad ?? primaryRecord?.chaserNorad,
    chaserCospar: chaserIdentity.cospar ?? primaryRecord?.chaserCospar,
    chaserSourceOfInformation:
      chaserIdentity.sourceOfInformation ??
      primaryRecord?.chaserSourceOfInformation,
    chaserOrbitUpdateTime:
      primaryRecord?.chaserOrbitUpdateTime ??
      toOptionalString(event?.chaser?.latestUpdate),
    minDistance: primaryRecord?.minDistance,
    radialDistance: primaryRecord?.radialDistance,
    alongTrackDistance: primaryRecord?.alongTrackDistance,
    crossTrackDistance: primaryRecord?.crossTrackDistance,
    missDistanceThreshold: primaryRecord?.missDistanceThreshold,
    radial: primaryRecord?.radial,
    radialThreshold: primaryRecord?.radialThreshold,
    along: primaryRecord?.along,
    alongTrackThreshold: primaryRecord?.alongTrackThreshold,
    cross: primaryRecord?.cross,
    crossTrackThreshold: primaryRecord?.crossTrackThreshold,
    probabilityThreshold: primaryRecord?.probabilityThreshold,
    collisionProbability: primaryRecord?.collisionProbability,
    maximumCollisionProbability: primaryRecord?.maximumCollisionProbability,
    scaledCollisionProbability: primaryRecord?.scaledCollisionProbability,
    poc: primaryRecord?.poc,
    mpoc: primaryRecord?.mpoc,
    spoc: primaryRecord?.spoc,
    conjunctions: normalizedConjunctions,
  };
};

export type EventMetricKey =
  | "minDistance"
  | "radial"
  | "along"
  | "cross"
  | "poc"
  | "mpoc"
  | "spoc";

type EventThresholdKey =
  | "missDistanceThreshold"
  | "radialThreshold"
  | "alongTrackThreshold"
  | "crossTrackThreshold"
  | "probabilityThreshold";

export type ThresholdMode = "absolute" | "lower" | "upper";

export interface EventMetricDefinition {
  id: EventMetricKey;
  thresholdId: EventThresholdKey;
  thresholdMode: ThresholdMode;
  label: string;
  miniLabel: string;
  graphTitle: string;
  axisTitle: string;
  thresholdTitle: string;
  tooltip: string;
  color: string;
  format: "number" | "probability";
  unit?: string;
  minValue: number | null;
  maxValue: number | null;
  primaryLabel?: string;
}

export const EVENT_EVOLUTION_METRICS = [
  {
    id: "minDistance",
    thresholdId: "missDistanceThreshold",
    thresholdMode: "lower",
    label: "Miss Distance",
    miniLabel: "Miss Dist.",
    graphTitle: "Miss Distance Evolution",
    axisTitle: "Distance (km)",
    thresholdTitle: "Miss distance threshold",
    tooltip: "Miss distance between objects",
    color: "#2563eb",
    format: "number",
    unit: "km",
    minValue: 0,
    maxValue: null,
  },
  {
    id: "radial",
    thresholdId: "radialThreshold",
    thresholdMode: "absolute",
    label: "Radial",
    miniLabel: "Radial",
    graphTitle: "Radial Distance Evolution",
    axisTitle: "Distance (km)",
    thresholdTitle: "Radial threshold",
    tooltip: "Radial distance",
    color: "#0f766e",
    format: "number",
    unit: "km",
    minValue: null,
    maxValue: null,
  },
  {
    id: "along",
    thresholdId: "alongTrackThreshold",
    thresholdMode: "absolute",
    label: "Along",
    miniLabel: "Along",
    graphTitle: "Along-track Distance Evolution",
    axisTitle: "Distance (km)",
    thresholdTitle: "Along-track threshold",
    tooltip: "Along-track distance",
    color: "#7c3aed",
    format: "number",
    unit: "km",
    minValue: null,
    maxValue: null,
  },
  {
    id: "cross",
    thresholdId: "crossTrackThreshold",
    thresholdMode: "absolute",
    label: "Cross",
    miniLabel: "Cross",
    graphTitle: "Cross-track Distance Evolution",
    axisTitle: "Distance (km)",
    thresholdTitle: "Cross-track threshold",
    tooltip: "Cross-track distance",
    color: "#b45309",
    format: "number",
    unit: "km",
    minValue: null,
    maxValue: null,
  },
  {
    id: "poc",
    thresholdId: "probabilityThreshold",
    thresholdMode: "upper",
    label: "PoC",
    miniLabel: "PoC",
    graphTitle: "Probability of Collision Evolution",
    axisTitle: "Probability",
    thresholdTitle: "Probability threshold",
    tooltip: "Probability of Collision",
    color: "#dc2626",
    format: "probability",
    minValue: 0,
    maxValue: null,
  },
  {
    id: "mpoc",
    thresholdId: "probabilityThreshold",
    thresholdMode: "upper",
    label: "MPoC",
    miniLabel: "MPoC",
    primaryLabel: "Max PoC",
    graphTitle: "Max Probability of Collision Evolution",
    axisTitle: "Probability",
    thresholdTitle: "Probability threshold",
    tooltip: "Max Probability of Collision",
    color: "#db2777",
    format: "probability",
    minValue: 0,
    maxValue: null,
  },
  {
    id: "spoc",
    thresholdId: "probabilityThreshold",
    thresholdMode: "upper",
    label: "SPoC",
    miniLabel: "SPoC",
    primaryLabel: "Scaled PoC",
    graphTitle: "Scaled Probability of Collision Evolution",
    axisTitle: "Probability",
    thresholdTitle: "Probability threshold",
    tooltip: "Scaled Probability of Collision",
    color: "#ea580c",
    format: "probability",
    minValue: 0,
    maxValue: null,
  },
] as const satisfies readonly EventMetricDefinition[];

export const MISS_DISTANCE_METRIC = EVENT_EVOLUTION_METRICS[0];
export const SEPARATION_METRICS = EVENT_EVOLUTION_METRICS.slice(1, 4);
export const PROBABILITY_METRICS = EVENT_EVOLUTION_METRICS.slice(4);

const CONJUNCTION_METRIC_FIELD_BY_EVENT_KEY: Record<EventMetricKey, string> = {
  minDistance: "missDistance.value",
  radial: "radialDistance.value",
  along: "alongTrackDistance.value",
  cross: "crossTrackDistance.value",
  poc: "collisionProbability.value",
  mpoc: "maximumCollisionProbability.value",
  spoc: "scaledCollisionProbability.value",
};

const PRIMARY_PROBABILITY_IDS: EventMetricKey[] = ["spoc", "poc", "mpoc"];

export const getRecordId = (record?: EventEvolutionEvent, index = 0) =>
  record?.bestCDM ??
  record?.conjunctionIdentifier ??
  record?.recordId ??
  record?.id ??
  `Record ${index + 1}`;

export const hasValidProbability = (value?: unknown) => {
  const text = getFieldText(value);

  if (text === undefined || text.trim() === "") {
    return false;
  }

  return Number(text) > 0;
};

export const formatProbability = (value?: unknown) => {
  const text = getFieldText(value);

  return hasValidProbability(text) ? text : "-";
};

export const parseEventNumber = (value?: unknown) => {
  const text = getFieldText(value);
  const numericValue = Number(text);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const getMetricById = (id: EventMetricKey) =>
  EVENT_EVOLUTION_METRICS.find((metric) => metric.id === id) ??
  MISS_DISTANCE_METRIC;

const isCriticalValue = (
  value?: unknown,
  threshold?: unknown,
  mode: ThresholdMode = "upper",
) => {
  const numericValue = parseEventNumber(value);
  const numericThreshold = parseEventNumber(threshold);

  if (numericValue === null || numericThreshold === null) {
    return false;
  }

  if (mode === "absolute") {
    return Math.abs(numericValue) <= Math.abs(numericThreshold);
  }

  if (mode === "lower") {
    return numericValue >= 0 && numericValue <= numericThreshold;
  }

  return numericValue >= numericThreshold;
};

export const formatMetricValue = (
  event: EventEvolutionEvent | undefined,
  metric: EventMetricDefinition,
  value = event?.[metric.id],
) =>
  metric.format === "probability"
    ? formatProbability(value)
    : (getFieldText(value) ? `${getFieldText(value)}${metric.unit ? ` ${metric.unit}` : ""}` : "-");

export const formatMetricValueWithConjunctionFormatters = (
  event: EventEvolutionEvent | undefined,
  metric: EventMetricDefinition,
  formatters?: FormatterMap,
  value = event?.[metric.id],
) => {
  const textValue = getFieldText(value);

  if (textValue === undefined) {
    return "-";
  }

  const formatterField = CONJUNCTION_METRIC_FIELD_BY_EVENT_KEY[metric.id];
  const formatter = formatterField ? formatters?.[formatterField] : undefined;

  return metric.format === "probability"
    ? formatValueWithOptionalUnit(textValue, formatter)
    : formatValueWithOptionalUnit(textValue, formatter, metric.unit);
};

export const formatMetricThresholdValue = (
  metric: EventMetricDefinition,
  threshold?: unknown,
) => {
  const formatted =
    metric.format === "probability"
      ? formatProbability(threshold)
      : (getFieldText(threshold) ? `${getFieldText(threshold)}${metric.unit ? ` ${metric.unit}` : ""}` : "-");

  return metric.thresholdMode === "absolute" && formatted !== "-"
    ? `+/- ${formatted}`
    : formatted;
};

export const formatMetricThreshold = (
  event: EventEvolutionEvent | undefined,
  metric: EventMetricDefinition,
) => formatMetricThresholdValue(metric, event?.[metric.thresholdId]);

export const getMetricTooltip = (
  event: EventEvolutionEvent | undefined,
  metric: EventMetricDefinition,
  baseTooltip = metric.tooltip,
) => `${baseTooltip}. ${metric.thresholdTitle}: ${formatMetricThreshold(event, metric)}`;

export const isMetricCritical = (
  event: EventEvolutionEvent | undefined,
  metric: EventMetricDefinition,
  value = event?.[metric.id],
) => isCriticalValue(value, event?.[metric.thresholdId], metric.thresholdMode);

export const getMetricTone = (
  event: EventEvolutionEvent | undefined,
  metric: EventMetricDefinition,
  value = event?.[metric.id],
) => (isMetricCritical(event, metric, value) ? "danger" : "default");

export const getPrimaryProbability = (event?: EventEvolutionEvent) => {
  const metric =
    PRIMARY_PROBABILITY_IDS.map(getMetricById).find((candidate) =>
      hasValidProbability(event?.[candidate.id]),
    ) ?? getMetricById("poc");

  return {
    label: metric.primaryLabel ?? metric.label,
    metric,
    tooltip: metric.tooltip,
    value: event?.[metric.id],
  };
};

export const getTimeValue = (value?: unknown) => {
  const normalizedValue = normalizeDateTime(value);

  if (!normalizedValue) return Number.NEGATIVE_INFINITY;

  const date = new Date(normalizedValue);

  return Number.isNaN(date.getTime())
    ? Number.NEGATIVE_INFINITY
    : date.getTime();
};

export const getEventDetectionTimes = (records: EventEvolutionEvent[] = []) =>
  records
    .map((record) => record.detectionTime ?? record.executionTime)
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => getTimeValue(a) - getTimeValue(b));

export const formatDate = (value?: unknown) => {
  const readableValue = normalizeDateTime(value);

  if (!readableValue) return "-";

  const date = new Date(readableValue);

  return Number.isNaN(date.getTime()) ? readableValue : date.toLocaleString();
};
